// src/pages/admin/AdminGuard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../../utils/firebase";
import bgLogin from "../../images-login/nen5.png";

/* ===================== apiBase detect ("" or http://localhost:3001) ===================== */
let _apiBase = null;
async function detectApiBase() {
  if (_apiBase !== null) return _apiBase;
  const candidates = ["", "http://localhost:3001"];
  for (const base of candidates) {
    try {
      const res = await fetch(`${base}/api/_selftest`);
      if (res.ok) {
        _apiBase = base;
        return _apiBase;
      }
    } catch {
      // try next
    }
  }
  _apiBase = "";
  return _apiBase;
}

/**
 * ✅ Check quyền admin bằng endpoint có thật trong server.js:
 * - GET /api/admin/users?limit=1
 * Nếu token hợp lệ + có role admin => 200 ok
 */
async function checkAdmin(token, timeoutMs = 12000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const apiBase = await detectApiBase();
    const res = await fetch(`${apiBase}/api/admin/users?limit=1`, {
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      signal: controller.signal,
    });

    let data = null;
    try {
      data = await res.json();
    } catch {}

    if (res.ok) return { ok: true, data };

    const msg = data?.message || data?.error || `Không truy cập được (HTTP ${res.status}).`;
    return { ok: false, status: res.status, message: msg };
  } catch (e) {
    if (e?.name === "AbortError") {
      return { ok: false, status: 504, message: "Không truy cập được (HTTP 504). (Timeout)" };
    }
    return { ok: false, status: 0, message: e?.message || "Lỗi mạng khi kiểm tra quyền admin." };
  } finally {
    clearTimeout(t);
  }
}

export default function AdminGuard({ children }) {
  const [phase, setPhase] = useState("checking"); // checking | need_login | checking_admin | forbidden | allowed | error
  const [message, setMessage] = useState("");

  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");

  const [busy, setBusy] = useState(false);
  const [currentEmail, setCurrentEmail] = useState("");

  // UI: popup login giống /analysis/login
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!auth) {
      setPhase("error");
      setMessage("Firebase Auth chưa sẵn sàng (auth = null).");
      setIsOpen(true);
      return;
    }

    const unsub = onAuthStateChanged(auth, async (u) => {
      setCurrentEmail(u?.email || "");

      if (!u) {
        setPhase("need_login");
        setMessage("");
        setIsOpen(true);
        return;
      }

      setPhase("checking_admin");
      setMessage("");

      try {
        // lấy ID token
        const token = await u.getIdToken(true);

        // check admin
        const r = await checkAdmin(token);

        if (r.ok) {
          setPhase("allowed");
          setMessage("");
          setIsOpen(false);
          return;
        }

        if (r.status === 403) {
          setPhase("forbidden");
          setMessage("Bạn không có quyền admin.");
          setIsOpen(true);
          return;
        }

        if (r.status === 401) {
          setPhase("need_login");
          setMessage(r.message || "Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.");
          setIsOpen(true);
          return;
        }

        setPhase("error");
        setMessage(r.message || "Lỗi không xác định.");
        setIsOpen(true);
      } catch (e) {
        setPhase("error");
        setMessage(e?.message || "Lỗi khi kiểm tra quyền admin.");
        setIsOpen(true);
      }
    });

    return () => unsub?.();
  }, []);

  const title = useMemo(() => {
    if (phase === "checking" || phase === "checking_admin") return "Đang kiểm tra...";
    return "Đăng nhập Admin";
  }, [phase]);

  const onLogin = async (e) => {
    e.preventDefault();
    setMessage("");

    const em = String(email || "").trim();
    const pw = String(pwd ?? "");

    if (!em || !pw) {
      setMessage("Vui lòng nhập đủ email và mật khẩu.");
      return;
    }

    try {
      setBusy(true);
      await signInWithEmailAndPassword(auth, em, pw);
      // onAuthStateChanged sẽ tự check admin, nếu ok sẽ đóng popup
    } catch (err) {
      const code = err?.code || "";
      let msg = err?.message || "Đăng nhập thất bại.";

      if (code === "auth/invalid-email") msg = "Email không đúng định dạng.";
      else if (code === "auth/user-not-found") msg = "Tài khoản không tồn tại.";
      else if (code === "auth/wrong-password") msg = "Sai mật khẩu.";
      else if (code === "auth/invalid-credential") msg = "Sai email hoặc mật khẩu.";
      else if (code === "auth/too-many-requests") msg = "Đăng nhập sai quá nhiều lần. Hãy chờ một lúc rồi thử lại.";
      else if (code === "auth/network-request-failed") msg = "Lỗi mạng. Kiểm tra internet rồi thử lại.";

      setMessage(msg);
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      setPhase("error");
      setMessage(e?.message || "Logout thất bại.");
      setIsOpen(true);
    }
  };

  if (phase === "allowed") return <>{children}</>;

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundImage: `url(${bgLogin})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
      }}
    >
      {currentEmail ? (
        <button
          type="button"
          onClick={onLogout}
          style={{
            position: "absolute",
            top: 18,
            left: 18,
            background: "#e53935",
            color: "#fff",
            border: 0,
            borderRadius: 10,
            padding: "8px 14px",
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
            zIndex: 2,
          }}
          title="Đăng xuất"
        >
          Logout
        </button>
      ) : null}

      <button
        type="button"
        onClick={() => setIsOpen(true)}
        style={{
          position: "absolute",
          top: 18,
          right: 18,
          background: "#7CFC00",
          color: "#000",
          border: 0,
          borderRadius: 10,
          padding: "8px 14px",
          fontWeight: 800,
          cursor: "pointer",
          boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
          zIndex: 2,
        }}
      >
        Login
      </button>

      {isOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: 420,
              maxWidth: "100%",
              background: "rgba(10,10,10,0.92)",
              color: "#fff",
              borderRadius: 16,
              padding: 16,
              border: "2px solid #7CFC00",
              boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              style={{
                position: "absolute",
                top: 10,
                left: 10,
                width: 28,
                height: 28,
                borderRadius: 999,
                border: "1px solid rgba(124,252,0,0.5)",
                background: "rgba(0,0,0,0.35)",
                color: "#7CFC00",
                fontWeight: 900,
                cursor: "pointer",
                lineHeight: "26px",
              }}
              aria-label="Close"
              title="Đóng"
            >
              ×
            </button>

            <div
              style={{
                textAlign: "center",
                fontWeight: 900,
                letterSpacing: 1,
                marginTop: 12,
                marginBottom: 12,
                color: "#7CFC00",
                textTransform: "uppercase",
              }}
            >
              {title}
            </div>

            {message ? (
              <div
                style={{
                  marginBottom: 12,
                  padding: 10,
                  background: "#2a1111",
                  border: "1px solid #b93a3a",
                  borderRadius: 10,
                  fontSize: 13,
                  lineHeight: 1.4,
                }}
              >
                {message}
              </div>
            ) : null}

            {(phase === "checking" || phase === "checking_admin") && (
              <div style={{ marginBottom: 12, opacity: 0.9 }}>Đang kiểm tra quyền admin…</div>
            )}

            {(phase === "need_login" || phase === "forbidden" || phase === "error") && (
              <form onSubmit={onLogin}>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  autoComplete="username"
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.15)",
                    background: "rgba(255,255,255,0.08)",
                    color: "#fff",
                    marginBottom: 10,
                    outline: "none",
                  }}
                />

                <input
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder="Password"
                  type="password"
                  autoComplete="current-password"
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.15)",
                    background: "rgba(255,255,255,0.08)",
                    color: "#fff",
                    marginBottom: 12,
                    outline: "none",
                  }}
                />

                <button
                  type="submit"
                  disabled={busy}
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 10,
                    border: 0,
                    background: busy ? "#4b7d1f" : "#7CFC00",
                    color: "#000",
                    fontWeight: 800,
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  {busy ? "Đang đăng nhập..." : "Login"}
                </button>

                {phase === "forbidden" && currentEmail ? (
                  <button
                    type="button"
                    onClick={onLogout}
                    style={{
                      width: "100%",
                      marginTop: 10,
                      padding: 12,
                      borderRadius: 10,
                      border: 0,
                      background: "#e53935",
                      color: "#fff",
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    Logout
                  </button>
                ) : null}
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}